import { css } from '@emotion/react';

import * as React from 'react';

import { Button, PageNotification } from '@coursera/cds-core';

import { useDPMDialog } from 'bundles/degree-home/components/v3-dpm/context/DPMDialogContext';
import { renderGpa } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

import _t from 'i18n!nls/degree-home';

type Props = {
  variant: 'gpa' | 'grade';
  level: 'category' | 'curriculum';
  gpa?: number;
};

const getCategoryLevelWarningText = (variant: Props['variant'], gpa: string): string => {
  switch (variant) {
    case 'gpa':
      return _t('Your GPA of #{gpa} is lower than the minimum GPA required for this section of the curriculum.', {
        gpa,
      });
    case 'grade':
      return _t('Your grade for one or more courses in this section is lower than the required minimum passing grade.');
    default:
      return '';
  }
};

const getCurriculumLevelWarningText = (variant: Props['variant'], gpa: string): string => {
  switch (variant) {
    case 'gpa':
      return _t('Your GPA of #{gpa} is lower than the minimum GPA required for this curriculum.', {
        gpa,
      });
    case 'grade':
      return _t('Your grade for one or more courses is lower than the required minimum passing grade.');
    default:
      return '';
  }
};

const getWarningText = (variant: Props['variant'], level: Props['level'], gpa: string): string => {
  switch (level) {
    case 'category':
      return getCategoryLevelWarningText(variant, gpa);
    case 'curriculum':
      return getCurriculumLevelWarningText(variant, gpa);
    default:
      return '';
  }
};

const CompletionWarning: React.VFC<Props> = ({ variant, level, gpa = 0 }) => {
  const warningText = getWarningText(variant, level, renderGpa(gpa));
  const { openDialog } = useDPMDialog();

  if (!warningText) {
    return null;
  }

  return (
    <PageNotification
      severity="warning"
      inlineAction={
        <Button variant="ghost" onClick={() => openDialog('whereToGoFromHere')}>
          {_t('Where to go from here?')}
        </Button>
      }
    >
      {warningText}
    </PageNotification>
  );
};

export default CompletionWarning;
